import React, { useState } from 'react'
import { useData } from '../../Context/DataContext';
import { useAuth } from '../../Context/AuthContext';
import { MessageSquare, AlertCircle, Layout, HelpCircle, Send, CheckCircle, ChevronDown } from 'react-feather';

interface CategoryOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const SupportForm = () => {
  const { addSupportTicket, loadingNewTicket } = useData()
  const { currentProfile } = useAuth()

  const [category, setCategory] = useState<string | null>(null);
  const [message, setMessage] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const categories: CategoryOption[] = [
    {
      value: 'Technical Problem',
      label: 'Technical Issue',
      icon: <AlertCircle size={18} />,
      description: 'Bugs, errors, or system malfunctions'
    },
    {
      value: 'User Interface Problem',
      label: 'UI/UX Problem',
      icon: <Layout size={18} />,
      description: 'Design, layout, or navigation issues'
    },
    {
      value: 'General Problem',
      label: 'General Inquiry',
      icon: <HelpCircle size={18} />,
      description: 'Questions or other feedback'
    }
  ]

  const selectedCategory = categories.find(c => c.value === category);

  const handleCategorySelect = (value: string) => {
    setCategory(value);
    setIsOpen(false);
  }

  const handleTicketSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !message.trim()) return;

    const data = {
      email: currentProfile.email,
      message: message,
      name: currentProfile.name,
      status: true,
      subject: category,
    }
    addSupportTicket(data)
    setSubmitted(true);
    setTimeout(() => {
      setMessage('');
      setCategory(null);
      setSubmitted(false);
    }, 3000);
  }

  const isFormValid = category && message.trim().length > 10;

  return (
    <div className='flex flex-col items-center w-full min-h-screen bg-dark-bg py-8 px-4'>
      {/* Header Section */}
      <div className="w-full max-w-2xl mb-8">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-accent-orange via-accent-gold to-accent-orange p-[1px]">
          <div className="bg-dark-card rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-xl bg-accent-orange/20">
                <MessageSquare className="w-6 h-6 text-accent-orange" />
              </div>
              <h1 className="text-2xl font-bold text-white">Support Center</h1>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Encountered an issue? We're here to help. Submit a ticket and our team will get back to you as soon as possible.
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="w-full max-w-2xl">
        <div className="bg-dark-card border border-dark-border rounded-2xl p-6 shadow-xl">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 animate-fade-in">
              <div className="p-4 rounded-full bg-green-500/20 mb-4">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Ticket Submitted!</h3>
              <p className="text-gray-400 text-center">We've received your request and will respond shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleTicketSubmit} className="space-y-6">
              {/* Category Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Issue Category</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full flex items-center justify-between p-4 rounded-xl border transition-all duration-200 ${
                      isOpen
                        ? 'border-accent-orange bg-dark-input'
                        : 'border-dark-border bg-dark-input hover:border-gray-600'
                    }`}
                  >
                    {selectedCategory ? (
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-accent-orange/20 text-accent-orange">
                          {selectedCategory.icon}
                        </div>
                        <div className="text-left">
                          <p className="text-white font-medium">{selectedCategory.label}</p>
                          <p className="text-xs text-gray-500">{selectedCategory.description}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-500">Select a category...</span>
                    )}
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isOpen && (
                    <div className="absolute z-50 w-full mt-2 bg-dark-card border border-dark-border rounded-xl shadow-2xl overflow-hidden animate-fade-in">
                      {categories.map((cat) => (
                        <button
                          key={cat.value}
                          type="button"
                          onClick={() => handleCategorySelect(cat.value)}
                          className={`w-full flex items-center gap-3 p-4 hover:bg-dark-card-hover transition-colors ${
                            category === cat.value ? 'bg-accent-orange/10' : ''
                          }`}
                        >
                          <div className={`p-2 rounded-lg ${
                            category === cat.value
                              ? 'bg-accent-orange/20 text-accent-orange'
                              : 'bg-dark-border text-gray-400'
                          }`}>
                            {cat.icon}
                          </div>
                          <div className="text-left">
                            <p className={`font-medium ${category === cat.value ? 'text-accent-orange' : 'text-white'}`}>
                              {cat.label}
                            </p>
                            <p className="text-xs text-gray-500">{cat.description}</p>
                          </div>
                          {category === cat.value && (
                            <CheckCircle className="w-5 h-5 text-accent-orange ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Message Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-300">Describe Your Issue</label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Please provide as much detail as possible about the issue you're experiencing..."
                    rows={6}
                    className="w-full p-4 bg-dark-input border border-dark-border rounded-xl text-white placeholder-gray-500 resize-none focus:outline-none focus:border-accent-orange transition-colors"
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-gray-500">
                    {message.length} characters
                  </div>
                </div>
                {message.length > 0 && message.length < 10 && (
                  <p className="text-xs text-amber-500">Please provide more details (minimum 10 characters)</p>
                )}
              </div>

              {/* User Info Display */}
              <div className="p-4 bg-dark-input rounded-xl border border-dark-border">
                <p className="text-xs text-gray-500 mb-2">Submitting as:</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-accent-orange to-accent-gold flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {currentProfile.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">{currentProfile.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{currentProfile.email || 'No email'}</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isFormValid || loadingNewTicket}
                className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-all duration-200 ${
                  isFormValid && !loadingNewTicket
                    ? 'bg-gradient-to-r from-accent-orange to-accent-gold text-white hover:shadow-lg hover:shadow-accent-orange/25 hover:scale-[1.02]'
                    : 'bg-dark-border text-gray-500 cursor-not-allowed'
                }`}
              >
                {loadingNewTicket ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Ticket</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Need immediate assistance? Contact us at{' '}
            <a href="mailto:nasimo@premierhealthgroup.net" className="text-accent-orange hover:underline">
              nasimo@premierhealthgroup.net
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default SupportForm
